import { awaitJSON, SingleChannelLambdaFetch } from '@src/utils/fetch-utils';

// Ad-level Social performance joined to the creative-alerting assets, keyed by
// ad_name. Sourced from the SCP service's getChannelMetricsTableData endpoint:
// passing overrideDimensions + overrideMetrics together bypasses the table-preset
// machinery and returns exactly ad-grain rows. We hit it through the repo's
// SingleChannelLambdaFetch (bearer-auth via getAccessToken) rather than a raw
// fetch with the app session cookie.

export interface AdPerformanceMetrics {
  spend: number;
  impressions: number;
  clicks: number;
  revenue: number;
}

// A performance row plus the ad's campaign (a dimension, not a toggleable metric).
export interface AdPerformanceRow extends AdPerformanceMetrics {
  campaign: string;
}

export type PerformanceColumnKey = keyof AdPerformanceMetrics;

// Single source of truth for the table's performance columns and their toggles.
export const PERFORMANCE_COLUMNS: { key: PerformanceColumnKey; label: string; isMoney: boolean }[] =
  [
    { key: 'spend', label: 'Spend', isMoney: true },
    { key: 'impressions', label: 'Impressions', isMoney: false },
    { key: 'clicks', label: 'Clicks', isMoney: false },
    { key: 'revenue', label: 'Revenue', isMoney: true }
  ];

// fetches[colKey][metric] -> value. With overrides there is a single colKey.
// ad_group_id is the Meta ad set id — our direct join key to the insights data.
interface AdMetricRow {
  dimensions: { ad_group_id?: string | number; campaign_name?: string };
  fetches: Record<string, Record<string, number>>;
}

interface ChannelMetricsResponse {
  lastModified: string;
  data: AdMetricRow[];
}

const COMPANY = 'onceuponafarm';

// onceuponafarm's social-page preset, copied from a known-working request. The
// handler still loads a page preset (for date/filter/global defaults) even when
// overrideDimensions/overrideMetrics are supplied, so these must be valid ids.
const SOCIAL_PRESET = { pageID: 486, tableID: 959, filterID: 1588, kpi: 'Orders', lag: '7d' };

interface FetchSocialAdPerformanceOptions {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
}

const num = (value: number | undefined): number => (Number.isFinite(value) ? (value as number) : 0);

// Pull every ad-grain row for the date range (no ad-name/id filter) and let the
// caller join post-hoc. The dataset is small enough that the payload is cheap.
export const fetchSocialAdPerformance = async ({
  start,
  end
}: FetchSocialAdPerformanceOptions): Promise<Map<string, AdPerformanceRow>> => {
  const res = await SingleChannelLambdaFetch('/getChannelMetricsTableData', {
    params: {
      company: COMPANY,
      channel: 'social',
      end,
      start,
      ...SOCIAL_PRESET,
      sources: 'platform',
      groupedCategories: '{}',
      // overrideDimensions + overrideMetrics MUST be sent together, else the
      // endpoint throws. Values are physical column names; this bypasses the
      // preset's own dimension/metric lists. ad_group_id is the ad set id (our
      // join key); campaign_name gives us the real campaign for color-coding.
      overrideDimensions: 'ad_group_id,campaign_name',
      overrideMetrics: 'spend,impressions,clicks,revenue'
    }
  });

  const json = await awaitJSON<ChannelMetricsResponse>(res);

  // Keyed by ad_group_id (= ad set id) so it joins directly to our adset entity.
  const byAdGroupId = new Map<string, AdPerformanceRow>();
  (json.data ?? []).forEach(row => {
    const adGroupId = row.dimensions?.ad_group_id;
    const colKey = Object.keys(row.fetches ?? {})[0];
    const metrics = colKey ? row.fetches[colKey] : undefined;
    if (adGroupId === undefined || adGroupId === null || !metrics) return;
    byAdGroupId.set(String(adGroupId), {
      campaign: row.dimensions?.campaign_name ?? '',
      spend: num(metrics.spend),
      impressions: num(metrics.impressions),
      clicks: num(metrics.clicks),
      revenue: num(metrics.revenue)
    });
  });

  return byAdGroupId;
};
